import React from 'react';
import { Box, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { PAGE_SIZE_OPTIONS, pageCount, pageRange } from './pagination';

type TablePaginationProps = {
  total: number;
  /** Nombre de lignes cochées. Omis quand la table ne permet pas de sélection. */
  selectedCount?: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /**
   * Changement de taille de page. Omis quand la taille est fixe : le sélecteur
   * disparaît alors, plutôt que d'offrir un choix sans effet.
   */
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Actions de masse propres à la table, alignées à droite comme sur la maquette. Le pied
   * étant collé, elles restent atteignables quelle que soit la position de défilement.
   */
  action?: React.ReactNode;
};

/**
 * Pied de table commun : bornes affichées, taille de page, navigation, actions de masse.
 *
 * Collé en bas du conteneur qui défile. Une page vaut cent lignes par défaut, soit sept à
 * dix hauteurs d'écran : sans cela les actions de masse sortent du champ dès la première
 * molette, alors que c'est en parcourant la liste qu'on coche.
 *
 * Le collage est inerte tant que rien ne défile : sur une liste courte le pied ne bouge
 * pas d'un pixel. En revanche un `overflow`, un `transform`, un `filter` ou un `contain`
 * posé sur un ancêtre entre ce pied et le conteneur qui défile l'annulerait — sans erreur,
 * sans avertissement et sans test rouge. C'est aussi pourquoi le pied doit rester frère du
 * `TableContainer`, qui porte un `overflow-x` : y entrer rendrait le collage inopérant.
 */
export default function TablePagination({
  total,
  selectedCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  action
}: TablePaginationProps) {
  const { t } = useTranslation();

  const lastPage = pageCount(total, pageSize);
  const { from, to } = pageRange(total, page, pageSize);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        position: 'sticky',
        // Zéro, et non une valeur d'aération : tout écart laisserait sous le pied une
        // bande où les lignes défilent à découvert.
        bottom: 0,
        // Le pied ne bénéficie plus du padding bas du Paper une fois décollé : il rend
        // lui-même l'air au-dessus du liseré et sous les boutons.
        py: 1.5,
        // Opaque, sinon les lignes se lisent au travers.
        bgcolor: 'background.paper',
        // Tient lieu de bord haut : sans lui, la dernière ligne vient buter sur les boutons.
        borderTop: '1px solid',
        borderColor: 'divider'
        // Volontairement sans `zIndex` : un élément positionné passe déjà au-dessus des
        // lignes, et le mettre à 1 l'égalerait au bloc titre de l'écran. Étant plus loin
        // dans le DOM il l'emporterait, et la liste du sélecteur de client — qui s'ouvre
        // sans portail — passerait derrière le pied.
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          {[
            t('interventionCreation.pagination.results', { from, to, total }),
            // Le décompte de sélection ne s'affiche que si la table en permet une.
            selectedCount === undefined
              ? null
              : t('interventionCreation.pagination.selected', { total: selectedCount })
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
        {onPageSizeChange && (
          <TextField
            select
            size="small"
            value={pageSize}
            // Changer la taille de page remet au début : la page courante n'aurait pas
            // de sens dans le nouveau découpage.
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            inputProps={{ 'aria-label': t('interventionCreation.pagination.pageSize') }}
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {t('interventionCreation.pagination.perPage', { total: option })}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton
            size="small"
            disabled={page <= 1}
            aria-label={t('interventionCreation.pagination.previousPage')}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="caption" color="text.secondary">
            {t('interventionCreation.pagination.pageStatus', { page, total: lastPage })}
          </Typography>
          <IconButton
            size="small"
            disabled={page >= lastPage}
            aria-label={t('interventionCreation.pagination.nextPage')}
            onClick={() => onPageChange(page + 1)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Stack>

      {action}
    </Box>
  );
}
